import EXTERNAL_SEARCH_CONFIG from '@/app/config/search'
import { ROUTES } from '@/app/lib/constants'
import { loadLocaleFiles } from '@/app/lib/load-locale'
import { generateTypesenseScopedKey } from '@/app/modules/search/lib/keys'
import { getSearchClient } from '@/app/modules/search/lib/search-client'
import {
  buildInitialResults,
  sanitizeForSerialization,
} from '@/app/modules/search/lib/ssr'
import { SubCategoryPage } from '@/app/modules/search/pages/SubCategoryPage'
import { AvailableLanguages } from '@/app/modules/shared/providers/LanguageSelectionProvider'
import type {
  ISubcategoryPageCourseHit,
  ISubcategoryPageHit,
} from '@/types/search'
import { isBoom } from '@hapi/boom'
import { ObjectNotFound } from 'typesense/lib/Typesense/Errors'

const frontendSearchApiKey = process.env.NEXT_SEARCH_INDEX_FRONTEND_API_KEY

type SearchHit =
  | ISubcategoryPageHit
  | ISubcategoryPageCourseHit<AvailableLanguages>

export async function getServerSideProps(context) {
  const {
    params: { slug },
    locale,
  } = context

  try {
    const slugRegex = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

    if (!slugRegex.test(slug)) {
      return {
        notFound: true,
      }
    }

    if (!EXTERNAL_SEARCH_CONFIG.enabled || !frontendSearchApiKey) {
      return {
        redirect: {
          destination: ROUTES.search,
          permanent: false,
          locale,
        },
      }
    }

    const messages = await loadLocaleFiles(locale, [
      'navigation',
      'strings',
      'search-page',
      'error',
    ])

    const seed = Math.round(Math.random() * 100)

    const scopedApiKeys = {
      courses: generateTypesenseScopedKey(
        frontendSearchApiKey,
        JSON.stringify({
          filter_by: `published:=true&&$subcategories_courses($subcategories(slug:=${slug}&&is_active:=true))`,
        })
      ),
    }

    const searchClient = getSearchClient(frontendSearchApiKey, {
      collectionSpecificSearchParameters: {
        subcategories: {
          q: '*',
          query_by: 'slug',
          filter_by: `slug:=${slug}&&is_active:=true`,
          include_fields: 'id,name,slug',
          limit: 1,
        },
        courses: {
          q: '*',
          query_by: `name_${locale}`,
          limit: 15,
          include_fields: `id,name_${locale},learning_description_${locale},min_price,quality_score,education_provider,instructors,level`,
          exclude_fields: '$subcategories_courses(*,$subcategories(*))',
          sort_by: `$courses(quality_score:desc,_rand(${seed}):desc)`,
          'x-typesense-api-key': scopedApiKeys.courses,
        },
      },
    })

    const searchRequests = [
      { indexName: 'subcategories', params: { query: '' } },
      { indexName: 'courses', params: { query: '' } },
    ]

    const { results } = await searchClient.search<SearchHit>(searchRequests)

    if (!Array.isArray(results) || results.length !== 2) {
      return {
        notFound: true,
        props: {
          messages,
          locale,
        },
      }
    }

    const [subCategoryResult, coursesResult] = results

    const subCategory =
      'hits' in subCategoryResult &&
      Array.isArray(subCategoryResult.hits) &&
      subCategoryResult.hits.length > 0
        ? (subCategoryResult.hits[0] as ISubcategoryPageHit)
        : null

    if (!subCategory) {
      return {
        notFound: true,
        props: {
          messages,
          locale,
        },
      }
    }

    const csrSearchConfig = {
      courses: {
        q: '*',
        query_by: `name_${locale}`,
        limit: 15,
        include_fields: `id,name_${locale},learning_description_${locale},min_price,quality_score,education_provider,instructors,level`,
        exclude_fields: '$subcategories_courses(*,$subcategories(*))',
        sort_by: `$courses(quality_score:desc,_rand(${seed}):desc)`,
        'x-typesense-api-key': scopedApiKeys.courses,
      },
    }

    const initialResults = buildInitialResults(coursesResult, 'courses')

    return {
      props: {
        subCategory: sanitizeForSerialization(subCategory),
        csrSearchConfig,
        initialResults: sanitizeForSerialization(initialResults),
        locale,
        messages,
      },
    }
  } catch (e) {
    console.error(e)

    if (e instanceof ObjectNotFound) {
      return {
        notFound: true,
      }
    }

    if (isBoom(e, 401)) {
      return {
        redirect: {
          destination: ROUTES.logout,
          permanent: false,
          locale,
        },
      }
    } else if (isBoom(e, 403)) {
      return {
        redirect: {
          destination: `/${locale}${ROUTES.accessDenied}`,
          permanent: false,
          locale,
        },
      }
    }

    throw e
  }
}

export default SubCategoryPage
